import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import AddContactModal from './AddContactModal';
import ImportContactsModal from './ImportContactsModal';

// GraphQL Queries
const GET_CONTACTS = gql`
  query GetContacts(
    $page: Int
    $pageSize: Int
    $groupId: Int
    $isBlocked: Boolean
    $unsubscribed: String
    $sortBy: String
    $sortOrder: String
    $search: String
  ) {
    contacts(
      page: $page
      pageSize: $pageSize
      groupId: $groupId
      isBlocked: $isBlocked
      unsubscribed: $unsubscribed
      sortBy: $sortBy
      sortOrder: $sortOrder
      search: $search
    ) {
      contacts {
        id
        name
        email
        phone
        isBlocked
        unsubscribedWhatsapp
        unsubscribedEmail
        notes
        groups {
          id
          name
        }
        createdAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

const GET_GROUPS = gql`
  query GetGroups {
    contactGroups {
      id
      name
      contactCount
    }
  }
`;

const DELETE_CONTACT = gql`
  mutation DeleteContact($id: Int!) {
    deleteContact(id: $id)
  }
`;

const TOGGLE_BLOCK = gql`
  mutation ToggleBlock($id: Int!) {
    toggleBlockContact(id: $id) {
      id
      isBlocked
    }
  }
`;

function Contacts() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    groupId: null,
    isBlocked: null,
    unsubscribed: 'all',
    search: '',
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Queries
  const { data, loading, refetch } = useQuery(GET_CONTACTS, {
    variables: {
      page,
      pageSize,
      ...filters,
      sortBy,
      sortOrder,
    },
  });

  const { data: groupsData } = useQuery(GET_GROUPS);

  // Mutations
  const [deleteContact] = useMutation(DELETE_CONTACT, {
    onCompleted: () => refetch(),
  });

  const [toggleBlock] = useMutation(TOGGLE_BLOCK, {
    onCompleted: () => refetch(),
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      await deleteContact({ variables: { id } });
    }
  };

  const handleToggleBlock = async (id) => {
    await toggleBlock({ variables: { id } });
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingContact(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContacts(data.contacts.contacts.map(c => c.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (id) => {
    if (selectedContacts.includes(id)) {
      setSelectedContacts(selectedContacts.filter(cid => cid !== id));
    } else {
      setSelectedContacts([...selectedContacts, id]);
    }
  };

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      groupId: null,
      isBlocked: null,
      unsubscribed: 'all',
      search: '',
    });
    setPage(1);
  };

  return (
    <div className="p-4 md:p-10 max-w-[1600px] mx-auto animate-in fade-in duration-700">
      {/* Header Context */}
      <div className="bg-white rounded-[2rem] md:rounded-[3rem] shadow-sm border border-gray-100 p-6 md:p-12 mb-8 md:mb-12 overflow-hidden relative group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-500/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 relative z-10">
          <div>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-2 h-8 bg-bulky-blue-500 rounded-full"></div>
              <h2 className="font-medium">Master Contacts Matrix</h2>
            </div>
            <p className="text-bulky-navy-400 text-[10px] md:text-xs font-normal uppercase tracking-[0.2em] md:tracking-[0.3em] ml-6 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 text-bulky-blue-500">
                <span className="material-icons-round text-sm">groups_3</span>
                {data?.contacts.total || 0} Entities
              </span>
              <span className="w-1 h-1 bg-gray-300 rounded-full hidden md:block"></span>
              Centralized network identity management protocol
            </p>
          </div>
          <div className="grid grid-cols-2 md:flex items-center gap-3 md:gap-4 w-full lg:w-auto">
            <button
              onClick={() => setShowAddModal(true)}
              className="col-span-2 md:col-span-1 bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-8 md:px-10 py-4.5 rounded-[1.25rem] transition-all shadow-2xl shadow-bulky-blue-500/25 active:scale-95 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.2em]"
            >
              <span className="material-icons-round text-lg">person_add</span>
              <span>Create Entry</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-white hover:bg-gray-50 text-bulky-navy-600 font-normal px-6 py-4.5 rounded-[1.25rem] transition-all flex items-center justify-center gap-3 border-2 border-gray-100 text-[10px] uppercase tracking-wider shadow-sm active:scale-95"
            >
              <span className="material-icons-round text-lg">upload_file</span>
              <span>Import</span>
            </button>
            <button className="bg-white hover:bg-gray-50 text-bulky-navy-600 font-normal px-6 py-4.5 rounded-[1.25rem] transition-all flex items-center justify-center gap-3 border-2 border-gray-100 text-[10px] uppercase tracking-wider shadow-sm active:scale-95">
              <span className="material-icons-round text-lg">download_for_offline</span>
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Global Search Interface */}
      <div className="bg-white rounded-[2.5rem] md:rounded-[3rem] shadow-sm border border-gray-100 p-6 md:p-8 mb-8 md:mb-10 group">
        <div className="relative w-full max-w-3xl flex items-center">
          <span className="material-icons-round absolute left-6 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors">manage_search</span>
          <input
            type="text"
            placeholder="Search by identity markers (name, phone, email)..."
            value={filters.search}
            onChange={(e) => applyFilters({ ...filters, search: e.target.value })}
            className="w-full pl-16 pr-8 py-5 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 md:gap-12">
        {/* Filters Panel */}
        <div className="lg:col-span-1">
          <div className="bg-bulky-navy-50 rounded-[2.5rem] md:rounded-[3rem] border border-bulky-navy-100/50 p-8 md:p-10 sticky top-10">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-10 rounded-xl bg-bulky-blue-500 text-white flex items-center justify-center shadow-lg shadow-bulky-blue-500/20">
                <span className="material-icons-round text-xl">tune</span>
              </div>
              <h2 className="font-medium">Matrix Filter</h2>
            </div>

            <div className="space-y-10">
              <div className="relative group/input">
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">Assigned Collective</label>
                <div className="relative">
                  <select
                    value={filters.groupId || ''}
                    onChange={(e) => applyFilters({ ...filters, groupId: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full pl-5 pr-12 py-4.5 bg-white border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:outline-none transition-all font-normal text-bulky-navy-800 appearance-none shadow-sm cursor-pointer"
                  >
                    <option value="">Full Hierarchy</option>
                    {groupsData?.contactGroups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                  <span className="material-icons-round absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">unfold_more</span>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">Access Protocol</label>
                <div className="grid grid-cols-1 gap-2.5">
                  {[
                    { label: 'All Protocols', value: null, icon: 'hub' },
                    { label: 'Blocked Only', value: true, icon: 'block' },
                    { label: 'Active Only', value: false, icon: 'check_circle' }
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => applyFilters({ ...filters, isBlocked: opt.value })}
                      className={`px-5 py-4 rounded-[1.25rem] text-left font-normal text-[10px] uppercase tracking-wider transition-all flex items-center gap-3 ${filters.isBlocked === opt.value
                        ? 'bg-bulky-blue-500 text-white shadow-2xl shadow-bulky-blue-500/25'
                        : 'bg-white text-bulky-navy-600 hover:bg-gray-50 shadow-sm'
                        }`}
                    >
                      <span className="material-icons-round text-sm">{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">Digital Consent Status</label>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-2.5">
                  {[
                    { label: 'Global', value: 'all' },
                    { label: 'Compliant', value: 'none' },
                    { label: 'Opt-out: WA', value: 'whatsapp' },
                    { label: 'Opt-out: Email', value: 'email' }
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => applyFilters({ ...filters, unsubscribed: opt.value })}
                      className={`px-5 py-4 rounded-[1.25rem] text-left font-normal text-[10px] uppercase tracking-wider transition-all border-2 ${filters.unsubscribed === opt.value
                        ? 'bg-bulky-navy-800 text-white border-bulky-navy-800 shadow-xl'
                        : 'bg-transparent text-bulky-navy-400 border-gray-100 hover:border-bulky-navy-200'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={resetFilters}
                className="w-full flex items-center justify-center gap-3 py-5 rounded-[1.25rem] font-normal text-[10px] uppercase tracking-[0.2em] text-bulky-navy-400 hover:text-red-500 hover:bg-red-50 transition-all border-2 border-transparent"
              >
                <span className="material-icons-round text-lg">refresh</span>
                Flush Filters
              </button>
            </div>
          </div>
        </div>

        {/* Contacts Matrix Viewport */}
        <div className="lg:col-span-3 space-y-8">
          <div className="bg-white rounded-[2.5rem] md:rounded-[3rem] shadow-sm border border-gray-100 overflow-hidden">
            {/* Extended Controls Toolbar */}
            <div className="p-6 md:p-10 border-b border-gray-50 flex flex-col xl:flex-row justify-between items-center gap-8 bg-gray-50/20">
              <div className="flex flex-wrap items-center justify-center gap-8">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">Sequencing</span>
                  <div className="relative group">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-transparent font-normal text-[10px] text-bulky-blue-500 uppercase focus:outline-none cursor-pointer pr-5 appearance-none"
                    >
                      <option value="created_at">Timestamp</option>
                      <option value="name">Identity Designation</option>
                      <option value="phone">Network Marker</option>
                      <option value="email">Protocol Address</option>
                    </select>
                    <span className="material-icons-round absolute right-0 top-1/2 -translate-y-1/2 text-bulky-blue-500 text-xs pointer-events-none italic">expand_more</span>
                  </div>
                </div>
                <button
                  onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
                  className="w-10 h-10 rounded-xl bg-white border-2 border-bulky-blue-50 text-bulky-blue-500 flex items-center justify-center hover:bg-bulky-blue-500 hover:text-white transition-all active:scale-95 shadow-sm"
                >
                  <span className="material-icons-round text-base">{sortOrder === 'ASC' ? 'north' : 'south'}</span>
                </button>
              </div>

              <div className="flex items-center gap-6">
                <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">Grid Density</span>
                <div className="flex gap-1.5 bg-gray-100 p-1.5 rounded-[1.25rem]">
                  {[10, 25, 50].map(v => (
                    <button
                      key={v}
                      onClick={() => { setPageSize(v); setPage(1); }}
                      className={`w-12 h-10 rounded-[1rem] text-[10px] font-normal transition-all ${pageSize === v ? 'bg-white text-bulky-blue-500 shadow-xl shadow-black/5' : 'text-gray-400 hover:text-bulky-navy-800'}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Matrix Data Interface */}
            <div className="relative">
              {!loading && (
                <div className="hidden md:block overflow-x-auto custom-scrollbar">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-50 text-[10px] font-normal uppercase tracking-wider text-bulky-navy-400 bg-bulky-navy-50/30">
                        <th className="px-10 py-6 text-left w-20">
                          <label className="relative flex items-center cursor-pointer group/cb">
                            <input
                              type="checkbox"
                              checked={data?.contacts.contacts.length > 0 && selectedContacts.length === data?.contacts.contacts.length}
                              onChange={handleSelectAll}
                              className="hidden"
                            />
                            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${selectedContacts.length === data?.contacts.contacts?.length ? 'bg-bulky-blue-500 border-bulky-blue-500' : 'bg-white border-gray-300 group-hover/cb:border-bulky-blue-500'}`}>
                              {selectedContacts.length === data?.contacts.contacts?.length && <span className="material-icons-round text-white text-base">done</span>}
                            </div>
                          </label>
                        </th>
                        <th className="px-8 py-6 text-left">Identity Designation</th>
                        <th className="px-8 py-6 text-left">Network Parameters</th>
                        <th className="px-8 py-6 text-left">Cluster Affiliation</th>
                        <th className="px-8 py-6 text-center">Encryption Status</th>
                        <th className="px-10 py-6 text-right w-40">Directives</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 text-sm">
                      {data?.contacts.contacts.map(contact => {
                        const isSelected = selectedContacts.includes(contact.id);
                        return (
                          <tr key={contact.id} className={`group hover:bg-bulky-blue-50/30 transition-all duration-300 ${isSelected ? 'bg-bulky-blue-50/50' : ''}`}>
                            <td className="px-10 py-6">
                              <label className="relative flex items-center cursor-pointer group/cb">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectContact(contact.id)}
                                  className="hidden"
                                />
                                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-white text-bulky-blue-500 border-bulky-blue-500 shadow-inner' : 'bg-white border-gray-200 group-hover/cb:border-bulky-blue-500'}`}>
                                  {isSelected && <span className="material-icons-round text-base">done</span>}
                                </div>
                              </label>
                            </td>
                            <td className="px-8 py-6">
                              <div className="flex items-center gap-5">
                                <div className="w-12 h-12 rounded-2xl bg-bulky-navy-50 border border-gray-100 flex items-center justify-center text-bulky-navy-600 font-display font-normal text-lg shadow-inner group-hover:scale-110 transition-transform">
                                  {contact.name?.substring(0, 1) || '?'}
                                </div>
                                <div className="font-display font-normal text-bulky-navy-800 tracking-tight text-base group-hover:text-bulky-blue-600 transition-colors uppercase">{contact.name || 'Anonymous Marker'}</div>
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2.5 text-bulky-navy-700 font-normal tracking-tight">
                                  <span className="material-icons-round text-lg text-bulky-blue-500">wifi_tethering</span>
                                  {contact.phone}
                                </div>
                                {contact.email && (
                                  <div className="flex items-center gap-2.5 text-[10px] font-normal text-gray-400 lowercase tracking-wider opacity-70">
                                    <span className="material-icons-round text-sm">alternate_email</span>
                                    {contact.email}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="flex flex-wrap gap-2">
                                {contact.groups.length > 0 ? contact.groups.map(group => (
                                  <span key={group.id} className="px-3 py-1.5 bg-bulky-blue-50 text-bulky-blue-600 text-[9px] font-normal uppercase tracking-wider rounded-xl border border-bulky-blue-100/50 shadow-sm">
                                    {group.name}
                                  </span>
                                )) : <span className="text-[9px] font-normal text-gray-300 uppercase tracking-wider italic opacity-60">Null segment</span>}
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="flex justify-center flex-wrap gap-3">
                                {contact.isBlocked && (
                                  <div className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center border border-red-100 shadow-sm" title="Restriction Active">
                                    <span className="material-icons-round text-lg">encryption_lock</span>
                                  </div>
                                )}
                                {contact.unsubscribedWhatsapp && (
                                  <div className="w-9 h-9 rounded-xl bg-yellow-50 text-yellow-600 flex items-center justify-center border border-yellow-100 shadow-sm" title="WA Protocol Inactive">
                                    <span className="material-icons-round text-lg">no_sim</span>
                                  </div>
                                )}
                                {!contact.isBlocked && !contact.unsubscribedWhatsapp && (
                                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)] animate-pulse"></div>
                                )}
                              </div>
                            </td>
                            <td className="px-10 py-6 text-right">
                              <div className="flex justify-end gap-3 lg:opacity-0 group-hover:opacity-100 transition-all transform translate-x-4 group-hover:translate-x-0">
                                <button
                                  onClick={() => handleEdit(contact)}
                                  className="w-10 h-10 bg-bulky-blue-50 text-bulky-blue-500 rounded-xl flex items-center justify-center hover:bg-bulky-blue-500 hover:text-white transition-all shadow-sm active:scale-90"
                                >
                                  <span className="material-icons-round text-lg">edit_note</span>
                                </button>
                                <button
                                  onClick={() => handleToggleBlock(contact.id)}
                                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm active:scale-90 ${contact.isBlocked ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white' : 'bg-red-50 text-red-600 hover:bg-red-500 hover:text-white'}`}
                                >
                                  <span className="material-icons-round text-lg">{contact.isBlocked ? 'verified_user' : 'lock_reset'}</span>
                                </button>
                                <button
                                  onClick={() => handleDelete(contact.id)}
                                  className="w-10 h-10 bg-gray-50 text-gray-400 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm active:scale-90"
                                >
                                  <span className="material-icons-round text-lg">delete_sweep</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Mobile Adaptive Interface */}
              <div className="md:hidden divide-y divide-gray-100">
                {data?.contacts.contacts.map(contact => {
                  const isSelected = selectedContacts.includes(contact.id);
                  return (
                    <div key={contact.id} className={`p-8 bg-white transition-all duration-500 relative ${isSelected ? 'bg-bulky-blue-50/50' : ''}`}>
                      <div className="flex items-start justify-between mb-8">
                        <div className="flex items-center gap-5">
                          <label className="relative flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectContact(contact.id)}
                              className="hidden"
                            />
                            <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-bulky-blue-500 border-bulky-blue-500' : 'bg-gray-50 border-gray-200'}`}>
                              {isSelected && <span className="material-icons-round text-white text-lg">done</span>}
                            </div>
                          </label>
                          <div className="w-14 h-14 rounded-2xl bg-bulky-navy-50 border border-gray-100 flex items-center justify-center text-bulky-navy-600 font-display font-normal text-xl shadow-inner">
                            {contact.name?.substring(0, 1) || '?'}
                          </div>
                          <div>
                            <div className="font-display font-normal text-bulky-navy-800 text-base uppercase tracking-tight leading-tight mb-2">{contact.name || 'Anonymous Marker'}</div>
                            <div className="text-[10px] font-normal text-bulky-blue-500 uppercase tracking-[0.2em]">{contact.phone}</div>
                          </div>
                        </div>
                        <div className="flex gap-2.5">
                          {contact.isBlocked && <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]"></div>}
                          {contact.unsubscribedWhatsapp && <div className="w-2.5 h-2.5 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.5)]"></div>}
                          {!contact.isBlocked && !contact.unsubscribedWhatsapp && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2.5 mb-10 pl-12">
                        {contact.groups.length > 0 ? contact.groups.map(group => (
                          <span key={group.id} className="px-3 py-1.5 bg-white text-[9px] font-normal uppercase tracking-wider text-gray-500 rounded-xl border border-gray-100 shadow-sm">
                            {group.name}
                          </span>
                        )) : <span className="text-[9px] font-normal text-gray-300 uppercase tracking-wider italic opacity-60">Null Segment</span>}
                      </div>

                      <div className="flex gap-3 pl-12">
                        <button onClick={() => handleEdit(contact)} className="flex-1 bg-bulky-blue-50 py-4.5 rounded-[1.25rem] text-bulky-blue-500 border-2 border-bulky-blue-100/50 shadow-sm active:scale-95 transition-all outline-none flex items-center justify-center">
                          <span className="material-icons-round text-xl">edit_note</span>
                        </button>
                        <button onClick={() => handleToggleBlock(contact.id)} className={`flex-1 py-4.5 rounded-[1.25rem] border-2 shadow-sm active:scale-95 transition-all outline-none flex items-center justify-center ${contact.isBlocked ? 'bg-emerald-50 text-emerald-500 border-emerald-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                          <span className="material-icons-round text-xl">{contact.isBlocked ? 'verified_user' : 'lock_reset'}</span>
                        </button>
                        <button onClick={() => handleDelete(contact.id)} className="flex-1 bg-gray-50 py-4.5 rounded-[1.25rem] text-gray-400 border-2 border-gray-100 shadow-sm active:scale-95 transition-all outline-none flex items-center justify-center">
                          <span className="material-icons-round text-xl">delete_sweep</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Matrix Pagination Flow */}
            {!loading && data?.contacts.totalPages > 1 && (
              <div className="p-8 md:p-12 border-t border-gray-50 flex flex-col sm:flex-row justify-between items-center gap-8 bg-gray-50/20">
                <p className="text-[10px] font-normal text-gray-400 uppercase tracking-[0.3em]">
                  Showing <span className="text-bulky-navy-800 bg-white px-3 py-1.5 rounded-lg shadow-inner">{(page - 1) * pageSize + 1} - {Math.min(page * pageSize, data?.contacts.total || 0)}</span> <span className="mx-2 text-gray-200">/</span> <span className="text-bulky-navy-800 bg-white px-3 py-1.5 rounded-lg shadow-inner">{data?.contacts.total || 0}</span> Identities
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-12 h-12 rounded-[1rem] border-2 border-gray-100 bg-white flex items-center justify-center text-gray-400 hover:text-bulky-blue-500 hover:border-bulky-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm active:scale-90"
                  >
                    <span className="material-icons-round">chevron_left</span>
                  </button>
                  <div className="flex gap-2 h-12 items-center px-4 bg-white border-2 border-gray-100 rounded-[1.5rem] shadow-sm">
                    {[...Array(data?.contacts.totalPages || 0)].map((_, i) => {
                      if (data.contacts.totalPages > 5 && Math.abs(page - (i + 1)) > 2) {
                        if (i === 0 || i === data.contacts.totalPages - 1) return <span key={i} className="text-[10px] font-normal text-gray-200">...</span>;
                        return null;
                      }
                      return (
                        <button
                          key={i}
                          onClick={() => setPage(i + 1)}
                          className={`w-8 h-8 rounded-lg text-[10px] font-normal transition-all ${page === i + 1
                            ? 'bg-bulky-blue-500 text-white shadow-xl shadow-bulky-blue-500/25'
                            : 'text-gray-400 hover:text-bulky-navy-800 hover:bg-gray-50'
                            }`}
                        >
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(data?.contacts.totalPages || 1, p + 1))}
                    disabled={page === data?.contacts.totalPages}
                    className="w-12 h-12 rounded-[1rem] border-2 border-gray-100 bg-white flex items-center justify-center text-gray-400 hover:text-bulky-blue-500 hover:border-bulky-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm active:scale-90"
                  >
                    <span className="material-icons-round">chevron_right</span>
                  </button>
                </div>
              </div>
            )}

            {data?.contacts.contacts.length === 0 && !loading && (
              <div className="p-32 md:p-48 text-center bg-gray-50/20 group">
                <div className="w-24 h-24 bg-white shadow-2xl rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 text-gray-200 border border-gray-50 group-hover:scale-110 transition-transform duration-700">
                  <span className="material-icons-round text-6xl">person_search</span>
                </div>
                <h2 className="font-medium">No entities detected</h2>
                <p className="text-gray-400 text-[10px] font-normal uppercase tracking-[0.3em] mt-2 max-w-sm mx-auto leading-relaxed">Adjust your parameters or initialize a direct import to broaden the search matrix.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals Pipeline */}
      {showAddModal && (
        <AddContactModal
          onClose={handleCloseModal}
          onSuccess={() => {
            refetch();
            handleCloseModal();
          }}
          editingContact={editingContact}
        />
      )}
      {showImportModal && <ImportContactsModal onClose={() => setShowImportModal(false)} onSuccess={refetch} />}
    </div>
  );
}

export default Contacts;
